import { useEffect, useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router';
import type { FeedbackRating, SessionFeedbackForm } from '@conference/shared';
import { sessionFeedbackService } from '../services/sessionFeedbackService';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

export default function SessionFeedbackScreen({ target = 'session' }: { target?: 'session' | 'presentation' }) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<SessionFeedbackForm | null>(null);
  const [ratings, setRatings] = useState<Record<string, FeedbackRating>>({});
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showSavedConfirmation, setShowSavedConfirmation] = useState(false);

  const load = async () => {
    if (!id) return;
    const next = target === 'presentation'
      ? await sessionFeedbackService.getPresentationForm(id)
      : await sessionFeedbackService.getForm(id);
    setForm(next);
    setRatings(Object.fromEntries((next.response?.answers ?? []).map((answer) => [answer.question_id, answer.rating])));
    setComment(next.response?.comment ?? '');
  };
  useEffect(() => { load().catch(() => setError('Could not load feedback.')); }, [id]);

  const submit = async () => {
    if (!id || !form?.configuration_revision) return;
    if (form.questions.some((question) => !ratings[question.id])) {
      setError('Please rate every question.'); return;
    }
    setSaving(true); setError('');
    try {
      await (target === 'presentation' ? sessionFeedbackService.savePresentation : sessionFeedbackService.save)(id, {
        configuration_revision: form.configuration_revision,
        answers: form.questions.map((question) => ({ question_id: question.id, rating: ratings[question.id] })),
        comment: comment || null,
      });
      await load();
      setShowSavedConfirmation(true);
    } catch (caught) {
      const failure = caught as Error & { code?: string };
      if (failure.code === 'feedback_configuration_changed' || failure.code === 'feedback_closed') await load();
      setError(failure.message || 'Could not save feedback.');
    } finally { setSaving(false); }
  };

  if (!form) return <div className="size-full flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  const readOnly = form.state === 'closed' || form.state === 'unavailable';
  return <div className="size-full overflow-y-auto px-4 pt-12 pb-10" style={{ background: 'var(--app-bg)' }}>
    <button onClick={() => navigate(-1)} aria-label="Back" className="size-10 rounded-full mb-5" style={{ background: 'var(--app-surface)' }}><ArrowLeft className="mx-auto w-5" /></button>
    <h1 className="text-xl mb-2" style={{ color: 'var(--app-text)' }}>{readOnly ? `${target === 'presentation' ? 'Presentation' : 'Session'} feedback` : form.response ? 'Edit feedback' : 'Give feedback'}</h1>
    {form.privacy_notice && <p className="text-sm mb-5" style={{ color: 'var(--app-text-muted)' }}>{form.privacy_notice}</p>}
    {form.state === 'upcoming' && <p className="text-sm mb-5" style={{ color: 'var(--app-text-muted)' }}>Feedback opens at scheduled time.</p>}
    {form.questions.map((question) => {
      const headingId = `feedback-question-${question.id}`;
      return <section key={question.id} role="region" aria-labelledby={headingId} className="rounded-xl border p-4 mb-3" style={{ borderColor: 'var(--app-border)', background: 'var(--app-surface)' }}>
        <h2 id={headingId} className="text-sm font-medium mb-3" style={{ color: 'var(--app-text)' }}>{question.prompt}</h2>
        <div role="radiogroup" aria-labelledby={headingId} className="grid grid-cols-3 gap-2">{([3, 2, 1] as FeedbackRating[]).map((rating) => <label key={rating} className="min-w-0 px-1 text-center text-xs leading-tight min-h-12 flex flex-col justify-center rounded-lg border" style={{ borderColor: ratings[question.id] === rating ? 'var(--app-accent)' : 'var(--app-border)', color: 'var(--app-text-muted)' }}>
          <input className="sr-only" type="radio" name={question.id} checked={ratings[question.id] === rating} disabled={readOnly || form.state !== 'open'} onChange={() => setRatings({ ...ratings, [question.id]: rating })} />
          <span>{rating}</span><span className="break-words">{form.scale_labels?.[rating]}</span>
        </label>)}</div>
      </section>;
    })}
    <label className="block text-sm mt-5" style={{ color: 'var(--app-text)' }}>Written comment <span style={{ color: 'var(--app-text-muted)' }}>(optional)</span>
      <textarea aria-label="Written comment" disabled={readOnly || form.state !== 'open'} maxLength={4000} value={comment} onChange={(event) => setComment(event.target.value)} className="mt-2 w-full min-h-28 rounded-xl border p-3" style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }} />
    </label>
    <p className="text-xs text-right" style={{ color: 'var(--app-text-muted)' }}>{comment.length}/4000</p>
    {error && <p role="alert" className="text-sm mt-3 text-red-400">{error}</p>}
    {!readOnly && form.state === 'open' && <button disabled={saving} onClick={submit} className="w-full min-h-12 rounded-xl mt-5" style={{ background: 'var(--app-accent)', color: 'white' }}>{saving ? 'Saving…' : form.response ? 'Save changes' : 'Submit feedback'}</button>}
    <AlertDialog open={showSavedConfirmation} onOpenChange={setShowSavedConfirmation}>
      <AlertDialogContent className="rounded-2xl" style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}>
        <AlertDialogHeader>
          <AlertDialogTitle style={{ color: 'var(--app-text)' }}>Feedback saved</AlertDialogTitle>
          <AlertDialogDescription style={{ color: 'var(--app-text-muted)' }}>
            Thank you. Your feedback has been saved and can be edited while feedback remains open.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction asChild>
            <button onClick={() => navigate(target === 'presentation' ? `/paper/${id}` : `/session/${id}`)} className="w-full min-h-12 rounded-xl" style={{ background: 'var(--app-accent)', color: 'white' }}>
              Back to {target === 'presentation' ? 'presentation' : 'session'}
            </button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>;
}
